import { useMutation, useQuery } from "@apollo/client";
import {
  Button,
  Checkbox,
  createStyles,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Theme,
  WithStyles,
  withStyles,
} from "@material-ui/core";
import { TooltipIconButton } from "@wa/werkstoff-core";
import { useFormik } from "formik";
import { gql } from "graphql.macro";
import { useSnackbar } from "material-ui-snackbar-provider";
import { Plus } from "mdi-material-ui";
import React, { useCallback, useState } from "react";
import ActionBar from "../../components/ActionBar";
import {
  GetContractorsDocument,
  CreateContractorDocument,
  UpdateContractorDocument,
  RemoveContractorDocument,
  TravelCostCalculation,
  ContractorWorkType,
} from "../../generated/graphql";
import ContractorForm, {
  ContractorFormValue,
} from "./components/ContractorForm";

gql`
  fragment ContractorFragment on Contractor {
    id
    name
    travelCost
    travelCostType
    hourlyRate
    workType
  }
`;

gql`
  query GetContractors {
    contractors {
      ...ContractorFragment
    }
  }
`;

gql`
  mutation CreateContractor($contractor: ContractorInput!) {
    createContractor(contractor: $contractor) {
      ...ContractorFragment
    }
  }
`;

gql`
  mutation UpdateContractor($id: ID!, $contractor: ContractorInput!) {
    updateContractor(id: $id, contractor: $contractor) {
      ...ContractorFragment
    }
  }
`;

gql`
  mutation RemoveContractor($id: ID!) {
    removeContractor(id: $id) {
      id
    }
  }
`;

const styles = (theme: Theme) =>
  createStyles({
    tableRow: {
      cursor: "pointer",
    },
    placeholder: {
      textAlign: "center",
      padding: theme.spacing(3),
    },
    removeDialogButton: {
      color: theme.palette.error.dark,
      marginRight: "auto",
    },
    moreItems: {
      flex: 1,
      paddingLeft: theme.spacing(2),
      "& > .MuiFormControlLabel-label": {
        ...theme.typography.body2,
      },
    },
  });

function RoomsContainer({ classes }: WithStyles<typeof styles>) {
  const snackbar = useSnackbar();

  const { data } = useQuery(GetContractorsDocument);

  const [createContractor] = useMutation(CreateContractorDocument, {
    refetchQueries: [{ query: GetContractorsDocument }],
  });
  const [addAnotherItem, setAddAnotherItem] = useState(false);
  const [showAddItemDialog, setShowAddItemDialog] = useState(false);
  const handleCreateContractor = useCallback(
    async (value: ContractorFormValue) => {
      try {
        await createContractor({
          variables: {
            contractor: {
              name: value.name,
              travelCost: value.travelCost ?? 0,
              travelCostType: value.travelCostType,
              workType: value.workType,
              hourlyRate:
                value.workType === ContractorWorkType.Other
                  ? null
                  : value.hourlyRate ?? 0,
            },
          },
        });
        snackbar.showMessage(
          `Die Fremdfirma "${value.name}" wurde hinzugefügt.`
        );
      } catch (e) {
        snackbar.showMessage("Die Fremdfirma konnte nicht hinzugefügt werden.");
        console.error("Could not create packing material", e);
      }
    },
    [snackbar, createContractor]
  );
  const formikNewItem = useFormik<ContractorFormValue>({
    initialValues: {
      name: "",
      travelCost: 0,
      travelCostType: TravelCostCalculation.PerKilometer,
      workType: ContractorWorkType.Assembly,
    },
    onSubmit: async (value) => {
      await handleCreateContractor(value);
      if (addAnotherItem) {
        formikNewItem.resetForm();
      } else {
        setShowAddItemDialog(false);
      }
    },
  });

  const [updateContractor] = useMutation(UpdateContractorDocument, {
    refetchQueries: [{ query: GetContractorsDocument }],
  });
  const [editItemId, setEditItemId] = useState<string | null>(null);
  const [showEditItemDialog, setShowEditItemDialog] = useState(false);
  const formikEditItem = useFormik<ContractorFormValue & { id?: string }>({
    initialValues: {
      name: "",
      travelCost: 0,
      travelCostType: TravelCostCalculation.PerKilometer,
      workType: ContractorWorkType.Assembly,
    },
    onSubmit: async (value) => {
      try {
        await updateContractor({
          variables: {
            id: editItemId!,
            contractor: {
              name: value.name,
              travelCost: value.travelCost ?? 0,
              travelCostType: value.travelCostType,
              workType: value.workType,
              hourlyRate:
                value.workType === ContractorWorkType.Other
                  ? null
                  : value.hourlyRate ?? 0,
            },
          },
        });
        setShowEditItemDialog(false);
        setEditItemId(null);
        snackbar.showMessage(
          `Die Fremdfirma "${value.name}" wurde aktualisiert.`
        );
      } catch (e) {
        snackbar.showMessage(
          "Die Fremdfirma konnte nicht aktualisiert werden."
        );
      }
    },
  });

  const handleShowItem = useCallback(
    (e) => {
      const id = e.currentTarget.dataset.itemId;
      console.log({ id });
      setEditItemId(id);
      setShowEditItemDialog(true);
      formikEditItem.resetForm({
        values: data?.contractors.find((m) => m.id === id),
      });
    },
    [formikEditItem, data]
  );

  const [removeContractor] = useMutation(RemoveContractorDocument, {
    refetchQueries: [{ query: GetContractorsDocument }],
  });
  const handleDeleteContractor = useCallback(
    async (id: string) => {
      const material = data?.contractors.find((m) => m.id === id);
      if (!material) return;
      try {
        await removeContractor({ variables: { id } });
        setShowEditItemDialog(false);
        setEditItemId(null);
        snackbar.showMessage(
          `Die Fremdfirma "${material.name}" wurde entfernt.`,
          "Rückgängig",
          () => handleCreateContractor(material)
        );
      } catch (e) {
        snackbar.showMessage("Die Fremdfirma konnte nicht entfernt werden.");
      }
    },
    [data, snackbar, handleCreateContractor, removeContractor]
  );

  return (
    <>
      <ActionBar title="Fremdfirmen">
        <TooltipIconButton
          tooltip="Fremdfirma hinzufügen"
          onClick={() => {
            formikNewItem.resetForm();
            setShowAddItemDialog(true);
          }}
        >
          <Plus />
        </TooltipIconButton>
      </ActionBar>
      <Paper>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Bezeichnung</TableCell>
              <TableCell>Tätigkeit</TableCell>
              <TableCell>Stundensatz</TableCell>
              <TableCell>Anfahrt</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data?.contractors.map((contractor) => (
              <TableRow
                key={contractor.id}
                onClick={handleShowItem}
                hover
                data-item-id={contractor.id}
                className={classes.tableRow}
              >
                <TableCell>{contractor.name}</TableCell>
                <TableCell>{getWorkTypeName(contractor.workType)}</TableCell>
                <TableCell>
                  {contractor.hourlyRate
                    ? `${contractor.hourlyRate.toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })} €`
                    : null}
                </TableCell>
                <TableCell>
                  {contractor.travelCost
                    ? `${contractor.travelCost.toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })} ${
                        contractor.travelCostType ===
                        TravelCostCalculation.PerKilometer
                          ? "€/km"
                          : "€"
                      }`
                    : null}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>
      <Dialog
        open={showAddItemDialog}
        onClose={() => setShowAddItemDialog(false)}
        disableBackdropClick
        fullWidth
      >
        <DialogTitle>Fremdfirma hinzufügen</DialogTitle>
        <DialogContent>
          <ContractorForm formikConfig={formikNewItem} />
        </DialogContent>
        <DialogActions>
          <FormControlLabel
            control={
              <Checkbox
                checked={addAnotherItem}
                onChange={(e) => setAddAnotherItem(e.target.checked)}
                name="addAnotherItem"
                color="primary"
              />
            }
            label="Weitere Fremdfirmen hinzufügen"
            className={classes.moreItems}
          />
          <Button onClick={() => setShowAddItemDialog(false)}>Abbrechen</Button>
          <Button color="primary" onClick={formikNewItem.submitForm}>
            Hinzufügen
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={showEditItemDialog}
        onClose={() => setShowEditItemDialog(false)}
        disableBackdropClick
        fullWidth
      >
        <DialogTitle>Fremdfirma bearbeiten</DialogTitle>
        <DialogContent>
          <ContractorForm formikConfig={formikEditItem} />
        </DialogContent>
        <DialogActions>
          <Button
            className={classes.removeDialogButton}
            onClick={() => handleDeleteContractor(editItemId!)}
          >
            Entfernen
          </Button>
          <Button onClick={() => setShowEditItemDialog(false)}>
            Abbrechen
          </Button>
          <Button color="primary" onClick={formikEditItem.submitForm}>
            Übernehmen
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default withStyles(styles)(RoomsContainer);

function getWorkTypeName(workType: ContractorWorkType) {
  switch (workType) {
    case ContractorWorkType.Assembly:
      return "Montage";
    case ContractorWorkType.Carrier:
      return "Träger";
    default:
      return "Sonstiges";
  }
}
